import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { ICandidateSourcingFilters, FilterKey } from '../types';
import { addFilterValue, removeFilterValue } from './filterUtils';

interface UseFilterStateProps {
  filters: ICandidateSourcingFilters;
  isOpen: boolean;
}

export const useFilterState = ({ filters, isOpen }: UseFilterStateProps) => {
  const [localFilters, setLocalFilters] = useState<ICandidateSourcingFilters>(filters);
  const [newValues, setNewValues] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setNewValues({});
      setHasChanges(false);
    }
  }, [isOpen, filters]);

  useEffect(() => {
    const hasChangesMade = isEqual(localFilters, filters);
    setHasChanges(!hasChangesMade);
  }, [localFilters, filters]);

  const handleAddFilter = (key: FilterKey, value: string) => {
    if (!value.trim()) return;

    const updatedFilters = addFilterValue(localFilters, key, value);
    setLocalFilters(updatedFilters);
    setNewValues({ ...newValues, [key]: '' });
  };

  const handleRemoveFilter = (key: FilterKey, index?: number, subIndex?: number) => {
    const updatedFilters = removeFilterValue(localFilters, key, index, subIndex);
    setLocalFilters(updatedFilters);
  };

  const handleValueChange = (key: FilterKey, value: string) => {
    setNewValues({ ...newValues, [key]: value });
  };

  const resetState = () => {
    setLocalFilters(filters);
    setNewValues({});
  };

  return {
    localFilters,
    newValues,
    hasChanges,
    handleAddFilter,
    handleRemoveFilter,
    handleValueChange,
    resetState
  };
};
